import { Link } from "react-router-dom";
import {
  KeyRound,
  ClipboardCheck,
  MessageSquare,
  Users,
  FileText,
  Settings,
  Camera,
  PaintBucket,
  Ruler,
  ShieldCheck,
  ChevronRight,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServicesPage() {
  return (
    <div className="container mx-auto px-4 py-12 space-y-20">
      {/* Hero Section */}
      <section className="text-center max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold mb-6">Our Services</h1>
        <p className="text-xl text-muted-foreground mb-8">
          Comprehensive property solutions tailored to your needs. From finding
          the perfect rental to full property management services.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Button size="lg" asChild>
            <Link to="/contact">Get Started</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="#services">Explore Services</Link>
          </Button>
        </div>
      </section>

      {/* Main Services */}
      <section id="services" className="scroll-mt-16">
        <h2 className="text-3xl font-bold text-center mb-12">What We Offer</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <ServiceCard
            icon={<KeyRound className="h-10 w-10" />}
            title="Property Listings"
            description="Access thousands of curated properties that match your criteria with detailed information and virtual tours."
            link="/portfolio"
          />
          <ServiceCard
            icon={<ClipboardCheck className="h-10 w-10" />}
            title="Tenant Screening"
            description="Comprehensive background checks, credit reports, and rental history verification for landlords."
            link="/landlords"
          />
          <ServiceCard
            icon={<MessageSquare className="h-10 w-10" />}
            title="Consultation Services"
            description="Expert advice on property investment, rental market trends, and optimization strategies."
            link="/contact"
          />
          <ServiceCard
            icon={<Users className="h-10 w-10" />}
            title="Roommate Matching"
            description="Find compatible roommates through our advanced matching system to share your perfect space."
            link="/contact"
          />
          <ServiceCard
            icon={<FileText className="h-10 w-10" />}
            title="Legal Documentation"
            description="Professional lease agreements, addendums, and other legal documents customized to your needs."
            link="/contact"
          />
          <ServiceCard
            icon={<Settings className="h-10 w-10" />}
            title="Property Management"
            description="Complete management services including rent collection, maintenance coordination, and tenant relations."
            link="/landlords"
          />
        </div>
      </section>

      {/* Premium Services */}
      <section className="bg-muted rounded-3xl p-8 md:p-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">
              Premium Property Services
            </h2>
            <p className="text-lg mb-6">
              Our premium services go beyond the basics to ensure your property
              stands out in the market and delivers maximum value.
            </p>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Camera className="h-5 w-5 text-primary" />
                    <span>Professional Photography</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  High-quality professional photography that showcases your
                  property in the best light, including aerial drone shots for
                  larger properties and virtual 3D tours.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <PaintBucket className="h-5 w-5 text-primary" />
                    <span>Staging & Interior Design</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  Professional staging services to present your property at its
                  best. Our interior designers can also provide consultation for
                  renovations and updates.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Ruler className="h-5 w-5 text-primary" />
                    <span>Property Assessments</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  Detailed property assessments including market analysis,
                  rental potential evaluation, and improvement recommendations
                  to maximize your investment returns.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    <span>Premium Tenant Vetting</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  Our most comprehensive tenant screening process, including
                  enhanced background checks, employment verification, rental
                  history analysis, and personal interviews.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Button className="mt-6">Learn More</Button>
          </div>
          <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl">
            <img
              src="/placeholder.svg?height=600&width=800"
              alt="Premium property services"
              className="object-cover"
            />
          </div>
        </div>
      </section>

      {/* Service Packages */}
      <section>
        <h2 className="text-3xl font-bold text-center mb-4">
          Service Packages
        </h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          Choose the package that best fits your needs, whether you&apos;re a
          tenant looking for your dream home or a landlord managing multiple
          properties.
        </p>

        <Tabs defaultValue="tenant" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList>
              <TabsTrigger value="tenant">For Tenants</TabsTrigger>
              <TabsTrigger value="landlord">For Landlords</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="tenant" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <PricingCard
                title="Basic Search"
                price="Free"
                features={[
                  "Property search access",
                  "Save favorite listings",
                  "Email alerts for new properties",
                  "Basic property details",
                ]}
                buttonText="Get Started"
                variant="basic"
              />
              <PricingCard
                title="Premium Renter"
                price="$49"
                period="one-time fee"
                features={[
                  "All Basic Search features",
                  "Priority viewing bookings",
                  "Rental application processing",
                  "Direct landlord communication",
                  "Moving assistance coordination",
                ]}
                buttonText="Choose Plan"
                variant="featured"
              />
              <PricingCard
                title="Concierge"
                price="$199"
                period="one-time fee"
                features={[
                  "All Premium Renter features",
                  "Dedicated rental agent",
                  "Custom property hunting",
                  "Virtual tours for out-of-town renters",
                  "Lease negotiation assistance",
                  "Utility setup coordination",
                ]}
                buttonText="Choose Plan"
                variant="premium"
              />
            </div>
          </TabsContent>

          <TabsContent value="landlord" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <PricingCard
                title="Listing"
                price="$99"
                period="per month"
                features={[
                  "Property listing creation",
                  "Basic tenant screening",
                  "Listing syndication",
                  "Email support",
                ]}
                buttonText="Get Started"
                variant="basic"
              />
              <PricingCard
                title="Professional"
                price="$199"
                period="per month"
                features={[
                  "All Listing features",
                  "Advanced tenant screening",
                  "Lease document preparation",
                  "Rent collection",
                  "Maintenance coordination",
                  "24/7 emergency support",
                ]}
                buttonText="Choose Plan"
                variant="featured"
              />
              <PricingCard
                title="Full Service"
                price="8%"
                period="of monthly rent"
                features={[
                  "All Professional features",
                  "Complete property management",
                  "Financial reporting",
                  "Tax document preparation",
                  "Regular property inspections",
                  "Eviction handling if needed",
                  "Dedicated account manager",
                ]}
                buttonText="Choose Plan"
                variant="premium"
              />
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* How It Works */}
      <section>
        <h2 className="text-3xl font-bold text-center mb-4">How It Works</h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          Our straightforward process makes finding or managing your property
          simple and efficient.
        </p>

        <div className="relative">
          {/* Process timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-muted hidden md:block"></div>

          <div className="space-y-12 relative">
            <ProcessStep
              number="1"
              title="Initial Consultation"
              description="We start with understanding your specific needs, preferences, and goals for your property journey."
              image="/placeholder.svg?height=300&width=400"
              imageAlt="Initial consultation"
            />

            <ProcessStep
              number="2"
              title="Customized Service Plan"
              description="Based on your needs, we develop a tailored service package that addresses your specific requirements."
              image="/placeholder.svg?height=300&width=400"
              imageAlt="Customized service plan"
              reverse
            />

            <ProcessStep
              number="3"
              title="Implementation"
              description="Our team works diligently to execute the plan, whether it's finding your perfect rental or managing your property."
              image="/placeholder.svg?height=300&width=400"
              imageAlt="Implementation"
            />

            <ProcessStep
              number="4"
              title="Ongoing Support"
              description="We provide continuous assistance and adjustments as needed to ensure your complete satisfaction."
              image="/placeholder.svg?height=300&width=400"
              imageAlt="Ongoing support"
              reverse
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-muted rounded-3xl p-8 md:p-12">
        <h2 className="text-3xl font-bold text-center mb-4">
          Client Testimonials
        </h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          Hear what our clients have to say about our services.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <TestimonialCard
            quote="Homespaces took all the stress out of finding a new apartment. Their Premium Renter package was worth every penny for the time it saved me."
            author="Jennifer L."
            role="Tenant"
          />
          <TestimonialCard
            quote="As a property owner with multiple units, their Full Service package has been a game-changer. I no longer worry about vacancies or maintenance issues."
            author="Michael B."
            role="Property Investor"
          />
          <TestimonialCard
            quote="The Concierge service was perfect as I was relocating from another state. They found me exactly what I was looking for before I even arrived."
            author="Sarah T."
            role="Executive"
          />
        </div>
      </section>

      {/* FAQ */}
      <section>
        <h2 className="text-3xl font-bold text-center mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          Find answers to common questions about our services.
        </p>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="faq-1">
              <AccordionTrigger>What areas do you serve?</AccordionTrigger>
              <AccordionContent>
                We currently provide services in all major metropolitan areas
                across the United States, with focused expertise in New York,
                Chicago, Los Angeles, San Francisco, Boston, and Portland.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-2">
              <AccordionTrigger>
                How long does it typically take to find a rental property?
              </AccordionTrigger>
              <AccordionContent>
                For most clients, we&apos;re able to find suitable properties
                within 1-2 weeks. With our Concierge service, we can often
                expedite this process to just a few days depending on your
                requirements and the current market.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-3">
              <AccordionTrigger>
                What does property management include?
              </AccordionTrigger>
              <AccordionContent>
                Our property management services include tenant sourcing and
                screening, rent collection, maintenance coordination, regular
                property inspections, financial reporting, and handling tenant
                communications. Our Full Service package also includes legal
                assistance and tax documentation.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-4">
              <AccordionTrigger>
                Can I change or cancel my service package?
              </AccordionTrigger>
              <AccordionContent>
                Yes, our landlord service packages can be upgraded at any time.
                For downgrades or cancellations, we require 30 days notice.
                Tenant packages are one-time fees and non-refundable once
                services have begun.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="faq-5">
              <AccordionTrigger>
                How are tenant screening reports handled?
              </AccordionTrigger>
              <AccordionContent>
                We conduct comprehensive background checks that include credit
                history, criminal background, eviction history, and employment
                verification. All screening is conducted in compliance with Fair
                Housing laws and with appropriate consent from applicants.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground rounded-3xl p-8 md:p-12 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto">
          Contact us today to discuss how we can help you with your property
          needs.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">Contact Us</Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="bg-transparent border-primary-foreground hover:bg-primary-foreground/10"
            asChild
          >
            <Link to="/about">Learn More About Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
}

function ServiceCard({ icon, title, description, link }: ServiceCardProps) {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full text-primary mb-4">
          {icon}
        </div>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-grow">
        <p>{description}</p>
      </CardContent>
      <CardFooter>
        <Link to={link} className="group inline-flex items-center text-primary">
          Learn more
          <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
        </Link>
      </CardFooter>
    </Card>
  );
}

interface PricingCardProps {
  title: string;
  price: string;
  period?: string;
  features: string[];
  buttonText: string;
  variant?: "basic" | "featured" | "premium";
}

function PricingCard({
  title,
  price,
  period,
  features,
  buttonText,
  variant = "basic",
}: PricingCardProps) {
  return (
    <Card
      className={`flex flex-col h-full ${
        variant === "featured" ? "border-primary shadow-lg relative" : ""
      }`}
    >
      {variant === "featured" && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
            MOST POPULAR
          </span>
        </div>
      )}
      <CardHeader
        className={
          variant === "featured"
            ? "bg-primary/10"
            : variant === "premium"
            ? "bg-secondary"
            : ""
        }
      >
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription className="flex items-end gap-1 mt-2">
          <span className="text-3xl font-bold">{price}</span>
          {period && <span className="text-muted-foreground">{period}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <ul className="space-y-2 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center">
              <Check className="h-5 w-5 mr-2 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          variant={variant === "featured" ? "default" : "outline"}
        >
          {buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
}

interface ProcessStepProps {
  number: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  reverse?: boolean;
}

function ProcessStep({
  number,
  title,
  description,
  image,
  imageAlt,
  reverse = false,
}: ProcessStepProps) {
  return (
    <div
      className={`flex flex-col ${
        reverse ? "md:flex-row-reverse" : "md:flex-row"
      } items-center gap-8`}
    >
      <div className="md:w-1/2 relative">
        <div className="rounded-lg overflow-hidden shadow-md">
          <img
            src={image || "/placeholder.svg"}
            alt={imageAlt}
            width="400"
            height="300"
            className="w-full h-auto"
          />
        </div>
      </div>

      <div className="md:w-1/2 relative">
        {/* Circle marker for timeline */}
        <div className="absolute left-1/2 md:left-0 top-0 md:top-1/2 transform -translate-x-1/2 md:-translate-x-1/2 md:-translate-y-1/2 w-10 h-10 rounded-full bg-primary text-primary-foreground items-center justify-center font-bold z-10 hidden md:flex">
          {number}
        </div>

        <div
          className={`md:pl-8 ${reverse ? "md:pr-8 md:pl-0 text-right" : ""}`}
        >
          <span className="inline-flex md:hidden items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold mb-2">
            {number}
          </span>
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <p className="text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}

interface TestimonialCardProps {
  quote: string;
  author: string;
  role: string;
}

function TestimonialCard({ quote, author, role }: TestimonialCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5 inline text-yellow-500"
            >
              <path
                fillRule="evenodd"
                d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                clipRule="evenodd"
              />
            </svg>
          ))}
        </div>
        <p className="italic mb-4">{quote}</p>
        <div>
          <p className="font-semibold">{author}</p>
          <p className="text-sm text-muted-foreground">{role}</p>
        </div>
      </CardContent>
    </Card>
  );
}
